import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Loader2 } from "lucide-react";

export function DangerZone() {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirm: "RESET" });
      const c = data.counts || {};
      toast.success(`Reset complete — ${c.users_deleted || 0} users removed, ${c.facilities_reset || 0} facilities un-claimed.`);
      setOpen(false); setConfirm("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="mt-8 border border-[#F0C9C9] bg-[#FDF6F6] rounded-2xl p-5" data-testid="danger-zone">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-6 w-6 text-[#C0392B] shrink-0 mt-0.5" />
        <div className="flex-1">
          <h3 className="font-heading text-lg font-medium text-[#1C211F]">Reset data for launch</h3>
          <p className="text-sm text-[#5C6660] mt-1 mb-3 max-w-2xl">
            Permanently deletes all users (except admins), claims, inquiries, messages, SMS logs, analytics events, orders, and payment records, and reverts every facility to unclaimed. Facilities, admin accounts, settings, integrations, and blog posts are kept. This cannot be undone.
          </p>
          <Button onClick={() => setOpen(true)} className="bg-[#C0392B] hover:bg-[#A5321F] text-white rounded-xl" data-testid="open-reset-btn">
            Reset all business data
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setConfirm(""); }}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2 text-[#C0392B]"><AlertTriangle className="h-6 w-6" /> Confirm data reset</DialogTitle>
            <DialogDescription>
              This will permanently delete all users (except admins), claims, inquiries, messages, logs, events, orders and payments, and un-claim every facility. Type <b>RESET</b> to confirm.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="reset-confirm-input">Type RESET to confirm</Label>
              <Input id="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="RESET" data-testid="reset-confirm-input" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setOpen(false); setConfirm(""); }} className="rounded-xl border-[#E8E6DF]" data-testid="reset-cancel-btn">Cancel</Button>
              <Button onClick={reset} disabled={confirm !== "RESET" || loading} className="bg-[#C0392B] hover:bg-[#A5321F] text-white rounded-xl" data-testid="reset-confirm-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Permanently reset"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
